import React from 'react';
import { Check } from 'lucide-react';
import { scrollToElement } from '../utils/scrollToElement';
import { trackEvent } from '../utils/metaPixel';
const FINAL_PRICE = 6.36;
function handleCheckout() {
  trackEvent('AddToCart', {
    content_name: 'El Método del 1% - Bundle Completo',
    content_ids: ['53815682957678'],
    content_type: 'product',
    value: FINAL_PRICE,
    currency: 'USD'
  });
  window.location.assign('https://vuelveavivir.com/cart/53815682957678:1');
}
/**
 * Editorial 2-column hero (silent luxury, clean-girl biohacking).
 * Headline mirrors the ad creative for tight message match; right column
 * carries a large, cinematic photo of a serene woman in a sunlit kitchen —
 * the lifestyle the visitor is invited to step into.
 */
export function Hero() {
  return <section className="relative pt-24 md:pt-28 pb-12 md:pb-20 overflow-hidden bg-cream" data-id="element-80">
      <div className="max-w-6xl mx-auto px-6 md:px-12 w-full grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center" data-id="element-81">
        {/* TEXT */}
        <div className="flex flex-col items-start text-left lg:col-span-6 order-1" data-id="element-82">
          <span className="uppercase tracking-[0.22em] text-[11px] md:text-xs font-medium text-sand-dark mb-5" data-id="element-83">
            Para mujeres con SOP, hipotiroidismo o síntomas inexplicables
          </span>

          <h1 className="font-serif text-[2.5rem] sm:text-5xl md:text-6xl lg:text-[4.25rem] leading-[1.05] text-ink mb-5 md:mb-6" data-id="element-84">
            Todo <em className="italic text-sand-dark font-normal" data-id="element-85">normal</em>.
            Pero sigues{' '}
            <em className="italic text-sand-dark font-normal" data-id="element-86">agotada</em>.
          </h1>

          <p className="font-serif text-lg md:text-2xl text-ink-light italic mb-5 md:mb-6 leading-snug" data-id="element-87">
            El problema no está en tus exámenes.
          </p>

          <p className="text-base md:text-lg text-ink-light font-light leading-relaxed mb-6 md:mb-7 max-w-lg" data-id="element-88">
            <span className="text-ink font-medium" data-id="element-89">El Método del 1%</span> es un
            protocolo de raíz, diseñado para ese espacio entre{' '}
            <em className="italic" data-id="element-90">enferma</em> y{' '}
            <em className="italic" data-id="element-91">bien</em> — donde los análisis salen bien
            pero tú sigues sin sentirte tú misma. Recupera tu energía, balance y
            claridad de forma natural, sin dietas extremas, sin suplementos
            costosos y sin esperar otro diagnóstico.
          </p>

          <ul className="space-y-2 mb-8 md:mb-10" data-id="element-92">
            {['Para SOP e hipotiroidismo', 'Sin diagnóstico previo necesario', 'Protocolo de raíz, no parches temporales'].map(bullet => <li key={bullet} className="flex items-start gap-2.5 text-sm md:text-base text-ink-light font-light" data-id="element-93">
                <Check strokeWidth={2} className="w-4 h-4 mt-1 flex-shrink-0 text-sand-dark" data-id="element-94" />
                <span data-id="element-95">{bullet}</span>
              </li>)}
          </ul>

          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5 sm:gap-6 w-full sm:w-auto" data-id="element-96">
            <button type="button" onClick={handleCheckout} className="inline-flex items-center justify-center bg-sand hover:bg-sand-dark text-white px-8 py-4 text-sm font-medium tracking-widest uppercase transition-colors duration-300 active:scale-[0.98] w-full sm:w-auto" data-id="element-97">
              Quiero Cambiar — ${FINAL_PRICE.toFixed(2)}
            </button>
            <a href="#method" className="text-sm uppercase tracking-widest text-ink-light hover:text-ink transition-colors border-b border-transparent hover:border-ink pb-1 self-center" data-id="element-98">
              Cómo funciona
            </a>
          </div>

          {/* Bonus value stack — mirrors the line-item table inside the
           Bundle so the visitor sees what the offer contains right under
           the CTA, anchoring perceived value before they scroll. */}
          <div className="mt-7 md:mt-8 w-full max-w-md" data-id="element-99">
            <p className="uppercase tracking-[0.2em] text-[10px] md:text-xs text-bordo font-medium mb-3 md:mb-4 animate-bordo-flash" data-id="element-100">
              Incluye gratis hoy
            </p>
            <ul className="divide-y divide-ink/10 border-y border-ink/10" data-id="element-101">
              {[{
              label: 'Bono #1 · Protocolo SOS Antojos',
              price: '$97'
            }, {
              label: 'Bono #2 · Recetario Hormonal de 15 Min',
              price: '$97'
            }, {
              label: 'Bono #3 · Checklist de Supervivencia',
              price: '$67'
            }].map(bonus => <li key={bonus.label} className="py-2.5 md:py-3 flex items-center gap-3" data-id="element-102">
                  <span className="flex-1 text-sm md:text-[15px] text-ink font-light leading-snug min-w-0" data-id="element-103">
                    {bonus.label}
                  </span>
                  <span className="font-serif text-sm md:text-base text-ink-light tabular-nums flex-shrink-0 w-12 text-right" translate="no" style={{
                textDecoration: 'line-through',
                textDecorationColor: '#B89665',
                textDecorationThickness: '2px'
              }} data-id="element-104">
                    {bonus.price}
                  </span>
                  <span className="flex-shrink-0 w-16 text-center text-[10px] md:text-[11px] uppercase tracking-[0.16em] font-bold text-sand-dark bg-sand/15 px-2 py-0.5" data-id="element-105">
                    Gratis
                  </span>
                </li>)}
            </ul>
          </div>
        </div>

        {/* CINEMATIC IMAGE — serene woman in a sunlit kitchen */}
        <div className="relative w-full lg:col-span-6 order-2" data-id="element-106">
          <div className="relative" data-id="element-107">
            {/* Soft sage card behind, slightly rotated for an editorial feel */}
            <div className="absolute -inset-3 md:-inset-5 bg-sage/50 rounded-2xl rotate-[-2deg]" data-id="element-108"></div>
            <div className="relative aspect-[4/5] overflow-hidden rounded-xl shadow-soft-lg bg-white" data-id="element-109">
              <img src="https://cdn.magicpatterns.com/uploads/s3QwgX2TtZEFoHJr4H7BCk/foto_pagina_principal.png" sizes="(max-width: 640px) 90vw, (max-width: 1024px) 80vw, 540px" alt="Mujer meditando en un acantilado frente al mar al amanecer, bañada por los rayos del sol — el momento de paz tras reencontrarse con una misma" width={768} height={1365}
            // @ts-expect-error fetchpriority is valid HTML but typed loosely in React
            fetchpriority="high" loading="eager" decoding="async" className="w-full h-full object-cover object-center" data-id="element-110" />
            </div>
          </div>
        </div>
      </div>
    </section>;
}